import { Autocomplete, Box, CircularProgress, Icon, IconButton, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { SelectOptions } from "../CustomSelect/CustomSelect";
import { Pagination } from "hooks/usePaginatedResources";
import { AdditionalCost, Crew, EquipmentPiece } from "generated/graphql";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { equals, F } from "ramda";

const MultiSelectExtension = ({
  tags,
  setTags,
  options,
  onChange,
  loading,
  pagination,
  search,
  id,
  label,
  nameCheckFn,
}: {
  tags: string[] | number[];
  setTags: React.Dispatch<React.SetStateAction<any[]>>;
  options: SelectOptions;
  onChange: (value: any) => void;
  loading: boolean;
  pagination: Pagination;
  search: (v: string) => void;
  id: string | number;
  label: string;
  nameCheckFn: Record<string | number, EquipmentPiece | AdditionalCost | Crew>;
}) => {
  const [inputValue, setInputValue] = useState("");
  const [displayedOptions, setDisplayedOptions] = useState(options);
  const tagNamesRef = useRef<Record<string | number, string>>({});

  useEffect(() => {
    search("");
  }, []);

  useEffect(() => {
    if (options && !loading) setDisplayedOptions(options);
  }, [options, loading]);

  useEffect(() => {
    const updated = { ...tagNamesRef.current };
    for (const key of Object.keys(nameCheckFn)) {
      if (nameCheckFn[key]?.name) updated[key] = nameCheckFn[key].name;
    }
    tagNamesRef.current = updated;
  }, [nameCheckFn]);

  const observer = useRef<IntersectionObserver>();
  const lastOption = displayedOptions?.slice(-1)?.pop();
  const isLastOption = lastOption ? equals(lastOption) : F;

  const lastOptionElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && pagination.hasNextPage) pagination.loadMore();
      });
      if (node) observer.current.observe(node);
    },
    [pagination.hasNextPage]
  );

  const handleTagSelect = (tagId: string | number): void => {
    if (!tags.includes(tagId as never)) {
      setTags([...tags, tagId]);
    }
  };

  const handleRemoveTag = (tagId: string | number) => {
    const tagIndex = tags.indexOf(tagId as never);
    if (tagIndex === -1) return;
    const tagsCopy = [...tags];
    tagsCopy.splice(tagIndex, 1);
    setTags(tagsCopy);
  };

  const availableOptions = displayedOptions.filter((o) => !tags.includes(o.value as never));

  return (
    <>
      <MDBox display="flex" flexWrap="wrap" gap="4px" mb={tags.length ? "8px" : 0}>
        {tags.map((tag) => (
          <MDBox
            key={tag}
            pl={1}
            borderRadius="10px"
            sx={{ background: "#eee", display: "inline-flex", alignItems: "center" }}
          >
            <MDTypography variant="body2" sx={{ fontsize: 10, display: "inline" }}>
              {tagNamesRef.current[tag] || nameCheckFn?.[tag]?.name}
            </MDTypography>
            <IconButton onClick={() => handleRemoveTag(tag)}>
              <Icon fontSize="small" color="error">
                delete
              </Icon>
            </IconButton>
          </MDBox>
        ))}
      </MDBox>
      <Autocomplete
        filterOptions={(x) => x}
        options={availableOptions}
        getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
        isOptionEqualToValue={(option, value) =>
          Boolean(option?.value) && Boolean(value?.value) && option.value === value.value
        }
        inputValue={inputValue}
        onInputChange={(_, newValue, reason) => {
          if (reason === "input") {
            setInputValue(newValue);
            search(newValue ?? "");
          } else if (reason === "clear") {
            setInputValue("");
            search("");
          }
        }}
        onChange={(_, selected) => {
          if (!selected || typeof selected === "string") return;
          const val = selected.value;
          handleTagSelect(val);
          onChange(val);
          setInputValue("");
          search("");
        }}
        disableCloseOnSelect
        renderInput={(params) => (
          <TextField
            {...params}
            label={label ?? "Search"}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <React.Fragment key={option.value}>
            <Box
              component="li"
              {...(isLastOption(option) ? { ref: lastOptionElementRef } : {})}
              {...props}
            >
              {option.label}
            </Box>
            {loading && isLastOption(option) && (
              <Box component="li" sx={{ pointerEvents: "none" }}>
                <CircularProgress color="inherit" size={16} />
              </Box>
            )}
          </React.Fragment>
        )}
      />
    </>
  );
};

export default MultiSelectExtension;
